import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  Clear,
  GridViewOutlined,
  LocationOnOutlined,
  Refresh,
  Search,
  ViewListOutlined,
  WarehouseOutlined,
} from "@mui/icons-material";
import moment from "moment";

const VIEW_MODE_KEY = "location-picker-view";

const loadViewMode = () => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const saveViewMode = (mode) => {
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // Storage unavailable (private mode etc.) - the choice just won't persist
  }
};

// Freshness of the last data update, used as the card's status indicator
const STALE_AFTER_MINUTES = 15;

// Temporarily hide the status footer ("No data" / "Never updated") for clusters without a last update
const SHOW_NO_DATA_STATUS = false;

const getFreshness = (lastUpdate) => {
  if (!lastUpdate) {
    return { label: "No data", color: "var(--c-text-faint)", bg: "var(--c-muted)" };
  }
  const minutes = moment().diff(moment.utc(lastUpdate), "minutes");
  if (minutes <= STALE_AFTER_MINUTES) {
    return { label: "Live", color: "var(--c-success)", bg: "var(--c-success-bg)" };
  }
  return { label: "Stale", color: "var(--c-warning)", bg: "var(--c-warning-bg)" };
};

const StatusChip = ({ freshness }) => (
  <Chip
    label={freshness.label}
    size="small"
    icon={
      <Box
        component="span"
        sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: freshness.color }}
      />
    }
    sx={{
      height: 22,
      fontSize: 12,
      fontWeight: 500,
      backgroundColor: freshness.bg,
      color: freshness.color,
      "& .MuiChip-icon": { ml: 1 },
    }}
  />
);

const ClusterIcon = ({ isCurrent }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isCurrent ? "var(--c-accent-bg)" : "var(--c-muted)",
      color: isCurrent ? "primary.main" : "var(--c-text-3)",
    }}
  >
    <WarehouseOutlined fontSize="small" />
  </Box>
);

const ClusterCard = ({ cluster, isCurrent, onSelect }) => {
  const freshness = getFreshness(cluster.last_update);
  const updated = cluster.last_update ? moment.utc(cluster.last_update).local() : null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        borderColor: isCurrent ? "primary.main" : "var(--c-border)",
        borderWidth: isCurrent ? 2 : 1,
        transition: "box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          borderColor: isCurrent ? "primary.main" : "var(--c-border-strong)",
        },
        "&:hover .cluster-card-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <CardActionArea onClick={() => onSelect(cluster)} sx={{ p: 2, height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <ClusterIcon isCurrent={isCurrent} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {cluster.cluster_name}
              </Typography>
              {isCurrent && (
                <Chip label="Current" size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: "var(--c-text-muted)" }}>
              <LocationOnOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2" noWrap title={cluster.address}>
                {cluster.address || "No address"}
              </Typography>
            </Box>
          </Box>

          <ArrowForward
            className="cluster-card-arrow"
            sx={{
              color: "primary.main",
              fontSize: 20,
              opacity: 0,
              transform: "translateX(-4px)",
              transition: "opacity 150ms ease, transform 150ms ease",
            }}
          />
        </Box>

        {(updated || SHOW_NO_DATA_STATUS) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid var(--c-muted)",
          }}
        >
          <StatusChip freshness={freshness} />
          <Typography
            variant="caption"
            sx={{ color: "var(--c-text-faint)" }}
            title={updated ? updated.format("MM/DD/YYYY HH:mm") : undefined}
          >
            {updated ? `Updated ${updated.fromNow()}` : "Never updated"}
          </Typography>
        </Box>
        )}
      </CardActionArea>
    </Card>
  );
};

const SkeletonCard = () => (
  <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "var(--c-border)", p: 2 }}>
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Skeleton variant="rounded" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="60%" />
        <Skeleton width="85%" />
      </Box>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1.5 }}>
      <Skeleton variant="rounded" width={56} height={22} />
      <Skeleton width={110} />
    </Box>
  </Card>
);

const ClusterRow = ({ cluster, isCurrent, onSelect }) => {
  const freshness = getFreshness(cluster.last_update);
  const updated = cluster.last_update ? moment.utc(cluster.last_update).local() : null;

  return (
    <CardActionArea
      onClick={() => onSelect(cluster)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: "1px solid var(--c-muted)",
        backgroundColor: isCurrent ? "var(--c-accent-bg)" : "transparent",
        boxShadow: isCurrent ? "inset 3px 0 0 var(--c-accent)" : "none",
        "&:last-of-type": { borderBottom: "none" },
        "&:hover .cluster-card-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <ClusterIcon isCurrent={isCurrent} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {cluster.cluster_name}
          </Typography>
          {isCurrent && (
            <Chip label="Current" size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "var(--c-text-muted)" }}>
          <LocationOnOutlined sx={{ fontSize: 14 }} />
          <Typography variant="body2" noWrap title={cluster.address}>
            {cluster.address || "No address"}
          </Typography>
        </Box>
      </Box>

      {(updated || SHOW_NO_DATA_STATUS) && (
        <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 2, flexShrink: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: "var(--c-text-faint)", minWidth: 150, textAlign: "right" }}
            title={updated ? updated.format("MM/DD/YYYY HH:mm") : undefined}
          >
            {updated ? `Updated ${updated.fromNow()}` : "Never updated"}
          </Typography>
          <StatusChip freshness={freshness} />
        </Box>
      )}

      <ArrowForward
        className="cluster-card-arrow"
        sx={{
          color: "primary.main",
          fontSize: 20,
          flexShrink: 0,
          opacity: 0,
          transform: "translateX(-4px)",
          transition: "opacity 150ms ease, transform 150ms ease",
        }}
      />
    </CardActionArea>
  );
};

const SkeletonRow = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderBottom: "1px solid var(--c-muted)" }}>
    <Skeleton variant="rounded" width={40} height={40} />
    <Box sx={{ flex: 1 }}>
      <Skeleton width="30%" />
      <Skeleton width="50%" />
    </Box>
    <Skeleton variant="rounded" width={56} height={22} />
  </Box>
);

const ClusterPicker = ({ data, loading, selectedCluster, onSelect, onRefresh }) => {
  const [filterText, setFilterText] = useState("");
  const [viewMode, setViewMode] = useState(loadViewMode);

  const handleViewModeChange = (_, mode) => {
    if (!mode) return; // Keep one option selected
    setViewMode(mode);
    saveViewMode(mode);
  };

  const filteredItems = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return data;
    return data.filter((item) =>
      [item.cluster_name, item.address].some((field) => (field || "").toLowerCase().includes(query))
    );
  }, [data, filterText]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Locations
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--c-text-muted)" }}>
            {loading
              ? "Loading locations…"
              : `${filteredItems.length} of ${data.length} location${data.length === 1 ? "" : "s"}`}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name or address"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 14 },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "var(--c-text-faint)" }} />
                  </InputAdornment>
                ),
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilterText("")} aria-label="Clear search">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={onRefresh}
            disabled={loading}
            sx={{ borderRadius: "10px", textTransform: "none", borderColor: "var(--c-border)", color: "var(--c-text-2)" }}
          >
            Refresh
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={handleViewModeChange}
            aria-label="View mode"
            sx={{
              "& .MuiToggleButton-root": { borderColor: "var(--c-border)", px: 1 },
              "& .MuiToggleButton-root:first-of-type": { borderRadius: "10px 0 0 10px" },
              "& .MuiToggleButton-root:last-of-type": { borderRadius: "0 10px 10px 0" },
            }}
          >
            <ToggleButton value="grid" aria-label="Card view" title="Card view">
              <GridViewOutlined fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view" title="List view">
              <ViewListOutlined fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === "grid" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 2,
          }}
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : filteredItems.map((cluster) => (
                <ClusterCard
                  key={cluster.cluster_id}
                  cluster={cluster}
                  isCurrent={selectedCluster?.cluster_id === cluster.cluster_id}
                  onSelect={onSelect}
                />
              ))}
        </Box>
      ) : (
        (loading || filteredItems.length > 0) && (
          <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "var(--c-border)" }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : filteredItems.map((cluster) => (
                  <ClusterRow
                    key={cluster.cluster_id}
                    cluster={cluster}
                    isCurrent={selectedCluster?.cluster_id === cluster.cluster_id}
                    onSelect={onSelect}
                  />
                ))}
          </Card>
        )
      )}

      {!loading && filteredItems.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6, color: "var(--c-text-muted)" }}>
          <LocationOnOutlined sx={{ fontSize: 40, color: "var(--c-border-strong)" }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
            {data.length === 0 ? "No locations available" : "No matching locations"}
          </Typography>
          <Typography variant="body2">
            {data.length === 0
              ? "Try refreshing, or contact your administrator for access."
              : "Try a different name or address."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClusterPicker;
